import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, Mail, Phone, Building2, Save } from "lucide-react";
import {
  adminListCustomers, adminCreateCustomer, adminUpdateCustomer, adminDeleteCustomer, adminGetCustomer,
} from "../../lib/api";

function Field({ label, name, value, onChange, type = "text", textarea }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">{label}</span>
      <Comp
        type={type}
        name={name}
        value={value || ""}
        rows={textarea ? 2 : undefined}
        onChange={onChange}
        className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
      />
    </label>
  );
}

export default function CustomersPanel({ onToast }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => setList(await adminListCustomers());
  useEffect(() => { load(); }, []);

  const blank = { name: "", email: "", phone: "", company: "", address: "", city: "", country: "India", notes: "" };
  const [form, setForm] = useState(blank);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const startCreate = () => { setForm(blank); setCreating(true); setEditing(null); };
  const startEdit = (c) => { setForm(c); setEditing(c.id); setCreating(false); };
  const cancel = () => { setCreating(false); setEditing(null); setForm(blank); };

  const save = async () => {
    try {
      if (editing) await adminUpdateCustomer(editing, form);
      else await adminCreateCustomer(form);
      onToast?.("Customer saved");
      await load();
      cancel();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await adminDeleteCustomer(id);
      onToast?.("Deleted");
      await load();
    } catch (e) {
      onToast?.("Delete failed", "error");
    }
  };

  const openView = async (id) => {
    const d = await adminGetCustomer(id);
    setViewing(d);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Customers</h2>
        <button onClick={startCreate} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      {(creating || editing) && (
        <div className="mt-5 border border-[#11151C]/10 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[#11151C]">{editing ? "Edit customer" : "New customer"}</h3>
            <button onClick={cancel} className="text-[#11151C]/60 hover:text-[#11151C]"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Name" name="name" value={form.name} onChange={handleChange} />
            <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
            <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            <Field label="Company" name="company" value={form.company} onChange={handleChange} />
            <Field label="City" name="city" value={form.city} onChange={handleChange} />
            <Field label="Country" name="country" value={form.country} onChange={handleChange} />
            <div className="sm:col-span-2">
              <Field label="Address" name="address" value={form.address} onChange={handleChange} />
            </div>
            <div className="sm:col-span-2">
              <Field label="Notes" name="notes" value={form.notes} onChange={handleChange} textarea />
            </div>
          </div>
          <button onClick={save} className="lm-btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      )}

      <div className="mt-6 border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6]">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-[#11151C]/50">No customers yet. Add one to get started.</td></tr>
            )}
            {list.map((c) => (
              <tr key={c.id} className="border-t border-[#11151C]/5">
                <td className="p-3">
                  <button onClick={() => openView(c.id)} className="font-semibold text-[#11151C] hover:text-[#FF6B3D]">{c.name}</button>
                </td>
                <td className="p-3 text-xs text-[#11151C]/70">
                  <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</div>
                  {c.phone && <div className="flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {c.phone}</div>}
                </td>
                <td className="p-3 text-xs text-[#11151C]/70">
                  {c.company && <div className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.company}</div>}
                </td>
                <td className="p-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${c.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                    {c.status || "active"}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(c)} className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"><Edit3 className="w-4 h-4 text-[#11151C]" /></button>
                  <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="lm-font font-extrabold text-[#11151C] text-xl">{viewing.customer?.name}</h3>
              <button onClick={() => setViewing(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#11151C]/60 text-sm mt-1">{viewing.customer?.email}</p>
            <div className="mt-4">
              <h4 className="font-bold text-[#11151C] text-sm mb-2">Hosting accounts ({viewing.hosting?.length || 0})</h4>
              {viewing.hosting?.length ? (
                <ul className="space-y-2">
                  {viewing.hosting.map((h) => (
                    <li key={h.id} className="border border-[#11151C]/10 rounded-lg p-3 text-sm">
                      <div className="font-semibold">{h.domain_name} · {h.plan_name}</div>
                      <div className="text-xs text-[#11151C]/60">Status: {h.status} · Expires: {h.expires_at ? new Date(h.expires_at).toLocaleDateString() : "—"}</div>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[#11151C]/50 text-sm">No hosting yet.</p>}
            </div>
            <div className="mt-4">
              <h4 className="font-bold text-[#11151C] text-sm mb-2">Invoices ({viewing.invoices?.length || 0})</h4>
              {viewing.invoices?.length ? (
                <ul className="space-y-2">
                  {viewing.invoices.map((i) => (
                    <li key={i.id} className="border border-[#11151C]/10 rounded-lg p-3 text-sm flex items-center justify-between">
                      <span>{i.type} · {new Date(i.created_at).toLocaleDateString()}</span>
                      <span className="font-semibold">₹{i.total_inr} · {i.status}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[#11151C]/50 text-sm">No invoices yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
